// Consultation admin des feedbacks qualité de l'assistant IA.
// Pattern : aiConfigService (adminFetch + redirection sur 401).
import { toast } from "sonner";

const ADMIN_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/admin`;

export interface AdminFeedbackSummary {
  id: string;
  userId: string;
  userEmail: string | null;
  sessionId: string | null;
  rating: "up" | "down";
  comment: string | null;
  hasConversation: boolean;
  hasGeneratedCode: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminFeedbackDetail extends AdminFeedbackSummary {
  messageId: string;
  conversation: Array<{
    id?: string;
    role: "user" | "assistant";
    content: string;
    timestamp?: number;
  }> | null;
  generatedCode: string | null;
}

export interface FeedbackListParams {
  rating?: "up" | "down";
  search?: string;
  limit?: number;
  offset?: number;
}

async function adminFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: { "Content-Type": "application/json", ...options.headers },
  });
  if (response.status === 401) {
    toast.error("Session administrateur expirée. Redirection en cours...");
    setTimeout(() => {
      window.location.href = "/admin";
    }, 1000);
    throw new Error("Session expirée");
  }
  return response;
}

export const aiFeedbackAdminService = {
  list: async (
    params: FeedbackListParams,
  ): Promise<{ items: AdminFeedbackSummary[]; total: number }> => {
    const query = new URLSearchParams();
    if (params.rating) query.set("rating", params.rating);
    if (params.search) query.set("search", params.search);
    if (params.limit) query.set("limit", String(params.limit));
    if (params.offset) query.set("offset", String(params.offset));
    const res = await adminFetch(
      `${ADMIN_API_BASE}/ai-feedback?${query.toString()}`,
      { method: "GET" },
    );
    if (!res.ok) throw new Error("Échec du chargement des feedbacks");
    const body = (await res.json()) as {
      data: AdminFeedbackSummary[];
      total: number;
    };
    return { items: body.data, total: body.total };
  },

  get: async (id: string): Promise<AdminFeedbackDetail> => {
    const res = await adminFetch(`${ADMIN_API_BASE}/ai-feedback/${id}`, {
      method: "GET",
    });
    if (!res.ok) throw new Error("Échec du chargement du feedback");
    const body = (await res.json()) as { data: AdminFeedbackDetail };
    return body.data;
  },
};
